import { useState, useEffect } from "react";
import usePageMetadata from "@/hooks/usePageMetadata";
import { useLocation, useSearchParams } from "react-router-dom";
import { searchEquipmentWithNLP, getEquipmentData } from "@/services/searchService";
import { AISearchResult } from "@/services/equipment/aiSearchService";
import EquipmentCard from "@/components/EquipmentCard";
import MapComponent from "@/components/MapComponent";
import MapLegend from "@/components/map/MapLegend";
import FilterBar from "@/components/FilterBar";
import HybridView from "@/components/HybridView";
import SortDropdown from "@/components/SortDropdown";
import GearQuickFilterInput from "@/components/GearQuickFilterInput";
import { Button } from "@/components/ui/button";
import { Loader2, Search, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
// Note: AdvancedFilterDrawer and AdvancedFilterPills are handled in FilterBar component
import { AdvancedFilters } from "@/types/advancedFilters";
import { applyAdvancedFilters } from "@/utils/advancedFiltering";
import { filterGearByQuickQuery } from "@/utils/gearQuickFilter";
import { trackEvent } from "@/utils/tracking";

import { useEquipmentWithDynamicDistance } from "@/hooks/useEquipmentWithDynamicDistance";
import { useUserLocations } from "@/hooks/useUserLocations";
import useScrollToTop from "@/hooks/useScrollToTop";
import { useScrollToTopButton } from "@/hooks/useScrollToTopButton";
import { ScrollToTopButton } from "@/components/ScrollToTopButton";
import { PUBLIC_ROUTE_META } from "@/lib/seo/publicMetadata";
import { resolveStaticRouteSeo } from "@/lib/seo/policy.js";

const SearchResultsPage = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const routeSeo = resolveStaticRouteSeo("/search", location.search);
  usePageMetadata({
    ...PUBLIC_ROUTE_META["/search"],
    ...routeSeo,
  });
  const [results, setResults] = useState<AISearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"map" | "list" | "hybrid">("hybrid");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>("relevance");
  // Track whether the user explicitly changed the sort order so we don't overwrite it
  const [userSelectedSort, setUserSelectedSort] = useState(false);
  const [searchInput, setSearchInput] = useState(query);
  const [quickFilter, setQuickFilter] = useState("");
  const [isAISearch, setIsAISearch] = useState(false);
  const { toast } = useToast();

  const { data: userLocations = [] } = useUserLocations();
  const [resetCounter, setResetCounter] = useState(0);

  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    priceRanges: [],
    ratingRanges: [],
    featured: false,
    myFavorites: false
  });

  // Perform search when query changes
  useEffect(() => {
    let isMounted = true;

    const fetchResults = async () => {
      if (!query) {
        // If no query, get all equipment based on global app setting
        try {
          const equipmentResults = await getEquipmentData();
          if (!isMounted) return;
          // Convert Equipment[] to AISearchResult[] for consistency
          const aiSearchResults: AISearchResult[] = equipmentResults.map(item => ({
            ...item,
            ai_relevance_score: undefined,
            ai_reasoning: undefined
          }));
          setResults(aiSearchResults);
          setIsAISearch(false);
        } catch (error) {
          if (!isMounted) return;
          console.error("Failed to load equipment:", error);
          trackEvent("search_fallback_load_failed", {
            failure_reason: error instanceof Error ? error.message : "equipment_load_failed",
          });
          setResults([]);
        }
        return;
      }

      setIsLoading(true);
      setIsAISearch(true);
      try {
        // Get user location if query contains "near me" or lacks an explicit location
        let userLocation;
        const needsLocation =
          query.toLowerCase().includes('near me') || !/\bin\s+/i.test(query);
        if (needsLocation) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
            });
            userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            console.log('🌍 User location obtained for search:', userLocation);
          } catch (locationError) {
            console.log('📍 Could not get user location:', locationError);
          }
        }

        const equipmentResults = await searchEquipmentWithNLP(
          query,
          userLocation,
        );
        if (!isMounted) return;
        setResults(equipmentResults);
        trackEvent("search_query_results_loaded", {
          query,
          result_count: equipmentResults.length,
          used_geolocation: Boolean(userLocation),
        });
      } catch (error) {
        if (!isMounted) return;
        console.error("Search failed:", error);
        trackEvent("search_query_failed", {
          query,
          failure_reason: error instanceof Error ? error.message : "search_failed",
        });
        toast({
          title: "Search Error",
          description: "Failed to process your search. Please try again.",
          variant: "destructive",
        });
        setResults([]);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      isMounted = false;
    };
  }, [query, toast]);

  // Get equipment with dynamic distances
  const { equipment: equipmentWithDynamicDistances, isLocationBased } = useEquipmentWithDynamicDistance(results);

  // Set a sensible default sort after results load only if the user hasn't manually chosen a sort.
  // Always prefer "relevance" for search queries to prioritize content relevance over proximity.
  useEffect(() => {
    if (!query || userSelectedSort) return;
    setSortBy("relevance");
  }, [query, userSelectedSort]);

  // Quick filter is local-only and resets for each new main search query.
  useEffect(() => {
    setQuickFilter("");
  }, [query]);

  // Handler that records when a user explicitly changes the sort
  const handleSortChange = (value: string) => {
    setSortBy(value);
    setUserSelectedSort(true);
  };

  const handleCategoryChange = (category: string | null) => {
    setActiveCategory(category);

    if (viewMode === "hybrid") {
      setResetCounter((c) => c + 1);
    }
  };

  // Filter results by category if selected
  const categoryFilteredResults = activeCategory
    ? equipmentWithDynamicDistances.filter(item => item.category === activeCategory)
    : equipmentWithDynamicDistances;

  // Apply advanced filters
  const baseFilteredResults = applyAdvancedFilters(categoryFilteredResults, advancedFilters);

  // Apply client-side quick filter
  const quickFilteredResults = filterGearByQuickQuery(baseFilteredResults, quickFilter);

  // Sort results based on selected option
  const sortedResults = [...quickFilteredResults].sort((a, b) => {
    switch (sortBy) {
      case "price_asc":
        return a.price_per_day - b.price_per_day;
      case "price_desc":
        return b.price_per_day - a.price_per_day;
      case "distance":
        return a.distance - b.distance;
      case "rating":
        return b.rating - a.rating;
      default: // relevance - use AI score if available, otherwise use fallback score
        // For AI search results
        if (isAISearch && 'ai_relevance_score' in a && 'ai_relevance_score' in b) {
          const aScore = typeof a.ai_relevance_score === 'number' ? a.ai_relevance_score : 0;
          const bScore = typeof b.ai_relevance_score === 'number' ? b.ai_relevance_score : 0;
          return bScore - aScore;
        }
        // For fallback search results (what we'll actually use)
        if ('fallback_relevance_score' in a && 'fallback_relevance_score' in b) {
          const aScore = typeof a.fallback_relevance_score === 'number' ? a.fallback_relevance_score : 0;
          const bScore = typeof b.fallback_relevance_score === 'number' ? b.fallback_relevance_score : 0;
          return bScore - aScore;
        }
        return 0;
    }
  });

  // Add debug logging for distance sorting
  useEffect(() => {
    if (sortBy === "distance") {
      console.log('Sorting by distance. First 5 items:',
        sortedResults.slice(0, 5).map(item => ({
          name: item.name,
          distance: item.distance,
          category: item.category
        }))
      );
    }
    console.log('SearchResultsPage: sortBy changed ->', sortBy, 'isLocationBased:', isLocationBased);
  }, [sortBy, sortedResults, isLocationBased]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedQuery = searchInput.trim();
    if (trimmedQuery) {
      trackEvent("search_query_started", {
        query: trimmedQuery,
        entry_surface: "search_results_page",
      });
    }

    const nextParams = new URLSearchParams(searchParams);
    if (trimmedQuery) {
      nextParams.set("q", trimmedQuery);
    } else {
      nextParams.delete("q");
    }

    nextParams.delete("start");
    nextParams.delete("end");
    nextParams.delete("startDate");
    nextParams.delete("endDate");
    nextParams.delete("from");
    nextParams.delete("to");
    nextParams.delete("checkin");
    nextParams.delete("checkout");

    setSearchParams(nextParams);
  };

  const handleReset = () => {
    setSearchInput("");
    setQuickFilter("");
    setSearchParams({});
    setActiveCategory(null);
    setSortBy("relevance");
    setAdvancedFilters({ priceRanges: [], ratingRanges: [], featured: false, myFavorites: false });
    setResetCounter((c) => c + 1);
    toast({
      title: "Filters Reset",
      description: "All filters and search query have been cleared.",
    });
  };

  const handleAdvancedFiltersChange = (filters: AdvancedFilters) => {
    setAdvancedFilters(filters);
  };

  const handleRemovePriceRange = (rangeId: string) => {
    setAdvancedFilters(prev => ({
      ...prev,
      priceRanges: prev.priceRanges.filter(id => id !== rangeId)
    }));
  };

  const handleRemoveRatingRange = (rangeId: string) => {
    setAdvancedFilters(prev => ({
      ...prev,
      ratingRanges: prev.ratingRanges.filter(id => id !== rangeId)
    }));
  };

  const handleRemoveFeatured = () => {
    setAdvancedFilters(prev => ({
      ...prev,
      featured: false
    }));
  };

  const handleSuggestionClick = (searchQuery: string) => {
    // Clear existing filters and search, then perform new search
    setSearchInput(searchQuery);
    setQuickFilter("");
    setActiveCategory(null);
    setAdvancedFilters({ priceRanges: [], ratingRanges: [], featured: false, myFavorites: false });
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("q", searchQuery);
    nextParams.delete("start");
    nextParams.delete("end");
    nextParams.delete("startDate");
    nextParams.delete("endDate");
    nextParams.delete("from");
    nextParams.delete("to");
    nextParams.delete("checkin");
    nextParams.delete("checkout");
    setSearchParams(nextParams);
  };

  // Scroll to top on mount
  useScrollToTop();

  // Scroll to top button for list view
  const { showButton: showScrollButton, scrollToTop: scrollListToTop } = useScrollToTopButton({
    threshold: 300
  });

  // Convert AISearchResult[] to MapEquipment[] for the map component
  const mapEquipment = sortedResults
    .filter(item => item.location && typeof item.location.lat === 'number' && typeof item.location.lng === 'number')
    .map(item => ({
      id: item.id,
      name: item.name,
      category: item.category,
      price_per_day: item.price_per_day,
      currency_code: item.currency_code,
      location: {
        lat: item.location.lat,
        lng: item.location.lng,
      },
      ownerId: item.owner.id,
      ownerName: item.owner.name,
    }));

  // Get user locations that have equipment matching the current filters
  const ownerIds = Array.from(new Set(mapEquipment.map(item => item.ownerId)));
  const filteredUserLocations = userLocations.filter(user => ownerIds.includes(user.id));
  const isQuickFilterActive = quickFilter.trim().length > 0;

  return (
    <div className="min-h-screen">
      <div className="bg-muted py-5">
        <div className="container px-4 md:px-6">
          <div className="flex items-center gap-2 mb-4">
            <h1 className="text-3xl font-bold">Search Results</h1>
            {/* {isAISearch && (
              <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium text-primary">AI-Enhanced</span>
              </div>
            )} */}
          </div>

          {/* Search form */}
          <form onSubmit={handleSearch} className="mb-6 flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
                placeholder="What can I help you find?"
              />
            </div>
            <Button type="submit" disabled={isLoading || !searchInput.trim()}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Search"}
            </Button>
          </form>

          {query && (
            <div className="mb-2">
              <p className="text-sm text-muted-foreground">
                Showing results for: <span className="font-medium">{query}</span>
                {/* {isAISearch && <span className="ml-2 text-primary">✨ AI-powered search</span>} */}
              </p>
              {!isLoading && (
                <p className="text-sm">
                  {isQuickFilterActive
                    ? `Found ${sortedResults.length} of ${baseFilteredResults.length} items (quick filter)`
                    : `Found ${baseFilteredResults.length} ${baseFilteredResults.length === 1 ? "item" : "items"}`}
                </p>
              )}
            </div>
          )}

          {!query && !isLoading && (
            <div className="mb-2">
              <p className="text-sm text-muted-foreground">
                Showing all equipment
              </p>
              <p className="text-sm">
                {isQuickFilterActive
                  ? `Found ${sortedResults.length} of ${baseFilteredResults.length} items (quick filter)`
                  : `Found ${baseFilteredResults.length} ${baseFilteredResults.length === 1 ? "item" : "items"}`}
              </p>
            </div>
          )}

        </div>
      </div>

      <FilterBar
        activeCategory={activeCategory}
        setActiveCategory={handleCategoryChange}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onReset={handleReset}
        advancedFilters={advancedFilters}
        onAdvancedFiltersChange={handleAdvancedFiltersChange}
        onRemovePriceRange={handleRemovePriceRange}
        onRemoveRatingRange={handleRemoveRatingRange}
        onRemoveFeatured={handleRemoveFeatured}
        recentlyViewedRightContent={
          <GearQuickFilterInput
            value={quickFilter}
            onChange={setQuickFilter}
            onClear={() => setQuickFilter("")}
            placeholder="Filter shown gear..."
            className="sm:max-w-none"
          />
        }
      />

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <span className="ml-4 text-lg">Processing your search...</span>
        </div>
      ) : viewMode === "map" ? (
        <div className="h-[calc(100vh-14rem)] relative">
          <MapComponent
            activeCategory={activeCategory}
            searchQuery={query?.toLowerCase()}
            isEquipmentLoading={isLoading}
            ownerIds={ownerIds}
            viewMode={viewMode}
            filteredUserLocations={filteredUserLocations}
          />
          <MapLegend activeCategory={activeCategory} viewMode={viewMode} />
        </div>
      ) : viewMode === "hybrid" ? (
        <HybridView
          filteredEquipment={sortedResults}
          activeCategory={activeCategory}
          isLocationBased={isLocationBased}
          userLocations={filteredUserLocations}
          viewMode={viewMode}
          resetSignal={resetCounter}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          showRelevanceOption
          emptyMessage={
            isQuickFilterActive
              ? "No gear matches your quick filter. Clear the filter or try different terms."
              : undefined
          }
        />
      ) : (
        <div className="container px-4 md:px-6 pt-4 pb-8">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {isLocationBased && (
              <div className="text-sm text-muted-foreground">
                Distances calculated from your location
              </div>
            )}
            <div className="w-full lg:w-auto lg:ml-auto mt-2 lg:mt-0">
              <SortDropdown
                sortBy={sortBy}
                onSortChange={handleSortChange}
                showRelevanceOption
              />
            </div>
          </div>
          {sortedResults.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedResults.map((equipment) => (
                <div key={equipment.id} className="relative">
                  <EquipmentCard equipment={equipment} />
                  {'ai_relevance_score' in equipment &&
                   typeof equipment.ai_relevance_score === 'number' &&
                   equipment.ai_relevance_score > 70 && (
                    <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      {equipment.ai_relevance_score}%
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium mb-2">No equipment found</h3>
              <p className="text-muted-foreground mb-6">
                {isQuickFilterActive
                  ? "No gear matches your quick filter. Clear the filter or try different terms."
                  : "Try adjusting your search terms or browse our categories below."}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <Button variant="outline" onClick={() => handleSuggestionClick("DHD surfboard")}>
                  DHD Surfboards
                </Button>
                <Button variant="outline" onClick={() => handleSuggestionClick("advanced snowboard")}>
                  Advanced Snowboards
                </Button>
                <Button variant="outline" onClick={() => handleSuggestionClick("beginner mountain bike")}>
                  Beginner Mountain Bikes
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <section className="container px-4 md:px-6 pb-8">
        <div className="mt-6 rounded-2xl border bg-background p-6 shadow-sm">
          <h2 className="text-2xl font-semibold">Search Surfboards, Snowboards, Skis, and Bikes</h2>
          <p className="mt-3 text-muted-foreground">
            Search DemoStoke by model name, sport, city, or riding style to find gear that is actually available from local shops and riders.
          </p>
        </div>
      </section>

      {/* Scroll to top button - only show in list view */}
      {viewMode === "list" && (
        <ScrollToTopButton show={showScrollButton} onClick={scrollListToTop} />
      )}
    </div>
  );
};

export default SearchResultsPage;
